import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import unescapedText from 'I18n/utils/unescapedText';
import { Panel } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { CloseButton } from 'foundations-components/transitional/components/CloseButton';
import { HR } from 'foundations-components/transitional/components/HR';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/transitional/components/View';
import { Link } from 'foundations-components/transitional/components/Link';
import { MARS_LAYER } from 'foundations-components/transitional/constants';
import { useIsPillBorderRadius } from '../hooks/useIsPillBorderRadius';
import { usePortalNavigation } from '../hooks/usePortalNavigation';
import { getNavLabel } from '../constants/navLabels';
import { useLogo } from '../context/PortalDataContext';
import { getIsThemePreview } from '../utils/metadata';
import { PortalsSideNavIcon } from './PortalsNav/PortalsSideNavIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DRAWER_WIDTH = 320;
function NavDrawerLinkRow({
  item,
  history,
  onNavigate,
  variant
}) {
  var _item$href;
  const isPill = useIsPillBorderRadius();
  const handleClick = e => {
    if (history && item.isCurrent && item.path) {
      e.preventDefault();
      history.push(item.path);
      onNavigate();
    } else if (getIsThemePreview()) {
      e.preventDefault();
    }
  };
  return /*#__PURE__*/_jsx(View, {
    as: "li",
    backgroundColor: item.isActive ? 'fill-accent-neutral-subtle' : undefined,
    borderRadius: isPill ? '0' : '100',
    "data-test-id": `portals-nav-drawer-item-${item.id}`,
    children: /*#__PURE__*/_jsxs(Link, {
      href: (_item$href = item.href) !== null && _item$href !== void 0 && _item$href.startsWith('/') ? item.href : `//${item.href}`,
      external: false,
      use: "unstyled",
      display: "flex",
      alignItems: "center",
      gap: "300",
      inlineSize: "100%",
      paddingBlock: "300",
      paddingInlineStart: variant === 'child' ? '700' : '300',
      paddingInlineEnd: "300",
      onClick: handleClick,
      "aria-current": item.isActive ? 'page' : undefined,
      children: [variant === 'top-level' ? /*#__PURE__*/_jsx(PortalsSideNavIcon, {
        name: item.id
      }) : null, /*#__PURE__*/_jsx(View, {
        as: "span",
        typography: "body-100",
        color: "text-core-default",
        children: getNavLabel(item.id)
      })]
    })
  });
}
function NavDrawerSection({
  item,
  history,
  onNavigate
}) {
  return /*#__PURE__*/_jsxs(View, {
    as: "li",
    "data-test-id": `portals-nav-drawer-section-${item.id}`,
    children: [/*#__PURE__*/_jsxs(Flex, {
      as: "div",
      alignItems: "center",
      gap: "300",
      paddingBlock: "300",
      paddingInline: "300",
      children: [/*#__PURE__*/_jsx(PortalsSideNavIcon, {
        name: item.id
      }), /*#__PURE__*/_jsx(View, {
        as: "span",
        typography: "detail-100",
        color: "text-core-subtle",
        children: getNavLabel(item.id)
      })]
    }), /*#__PURE__*/_jsx(Flex, {
      as: "ul",
      flexDirection: "column",
      gap: "100",
      children: item.children.map(child => /*#__PURE__*/_jsx(NavDrawerLinkRow, {
        item: child,
        history: history,
        onNavigate: onNavigate,
        variant: "child"
      }, child.id))
    })]
  });
}
export function PortalsNavDrawer({
  open,
  onClose,
  history,
  currentPathname
}) {
  var _ref, _logo$alt;
  const {
    navItems
  } = usePortalNavigation(currentPathname);
  const logo = useLogo();
  const previouslyFocusedRef = useRef(null);

  // Capture the focused element at render time, before child Panel/Portal
  // mounts steal focus on their own commit. This must run during render so
  // that the captured element is the trigger that opened the drawer rather
  // than the panel surface.
  if (open && previouslyFocusedRef.current == null) {
    previouslyFocusedRef.current = document.activeElement;
  }
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const toFocus = previouslyFocusedRef.current;
      previouslyFocusedRef.current = null;
      if (toFocus && typeof toFocus.focus === 'function') {
        toFocus.focus();
      }
    };
  }, [open, onClose]);
  if (!open) return null;
  const backdrop = /*#__PURE__*/createPortal( /*#__PURE__*/_jsx(View, {
    onClick: onClose,
    "aria-hidden": "true",
    "data-test-id": "portals-nav-drawer-backdrop",
    position: "fixed",
    inset: "0",
    zIndex: MARS_LAYER,
    backgroundColor: "specialty-scrim"
  }), document.body);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [backdrop, /*#__PURE__*/_jsx(Panel, {
      align: "left",
      width: DRAWER_WIDTH,
      "aria-label": unescapedText('app.navDrawer.label'),
      "data-test-id": "portals-nav-drawer",
      children: /*#__PURE__*/_jsxs(View, {
        as: "div",
        inlineSize: "100%",
        id: "portals-nav-drawer",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          blockSize: "56px",
          paddingInline: "200",
          gap: "200",
          "data-test-id": "portals-nav-drawer-header",
          children: [/*#__PURE__*/_jsx(Flex, {
            flexShrink: "0",
            alignItems: "center",
            minInlineSize: "0",
            children: logo !== null && logo !== void 0 && logo.src ? /*#__PURE__*/_jsx("img", {
              src: logo.src,
              alt: (_ref = (_logo$alt = logo.alt) !== null && _logo$alt !== void 0 ? _logo$alt : logo.name) !== null && _ref !== void 0 ? _ref : 'Logo',
              style: {
                maxHeight: '32px',
                maxWidth: '120px'
              }
            }) : null
          }), /*#__PURE__*/_jsx(Flex, {
            flexGrow: "1"
          }), /*#__PURE__*/_jsx(CloseButton, {
            inline: true,
            "data-action": "close",
            onClick: onClose,
            color: "text-core-default",
            "aria-label": unescapedText('app.navDrawer.close'),
            "data-test-id": "portals-nav-drawer-close",
            marginInlineEnd: "200"
          })]
        }), /*#__PURE__*/_jsx(HR, {
          distance: "flush"
        }), /*#__PURE__*/_jsx(PanelBody, {
          children: /*#__PURE__*/_jsx(Flex, {
            as: "ul",
            flexDirection: "column",
            gap: "100",
            paddingInline: "200",
            paddingBlockEnd: "400",
            children: navItems.map(item => item.children.length > 0 ? /*#__PURE__*/_jsx(NavDrawerSection, {
              item: item,
              history: history,
              onNavigate: onClose
            }, item.id) : /*#__PURE__*/_jsx(NavDrawerLinkRow, {
              item: item,
              history: history,
              onNavigate: onClose,
              variant: "top-level"
            }, item.id))
          })
        })]
      })
    })]
  });
}